import React, { useMemo, useEffect, useState } from 'react';
import { pb } from '@/lib/pocketbase';
import { RecordModel } from 'pocketbase';
import { useSearchParams } from 'react-router-dom';

interface Translation {
  translations: {
    en: {
      title: string;
      description: string;
    };
    np: {
      title: string;
      description: string;
    };
  };
}

interface Feature extends RecordModel {
  title: string;
  description: string;
  image: string;
  product: string[];
  order: number;
}

interface FeatureCardProps {
  productId: string;
}

// Feature Modal component to show the pop-up
const FeatureModal: React.FC<{ 
  feature: Feature | null;
  translations?: Translation;
  locale: string;
  onClose: () => void;
}> = ({ feature, translations, locale, onClose }) => {
  if (!feature) return null;

  const imageUrl = useMemo(() => {
    try {
      return pb.files.getURL(feature, feature.image);
    } catch (error) {
      console.error('Error generating image URL:', error);
      return '/placeholder.jpg';
    }
  }, [feature]);

  const getTranslatedContent = (field: 'title' | 'description') => {
    if (locale === 'en') {
      return feature[field];
    }
    return translations?.translations?.[locale]?.[field] || feature[field];
  };

  const getFontClass = () => {
    return locale === 'np' ? 'font-noto' : 'font-slate';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div 
        className="relative max-w-3xl w-[90%] bg-white rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          className="absolute right-4 top-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition"
          onClick={onClose}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div className="h-[300px] sm:h-[400px] w-full">
          <img 
            src={imageUrl} 
            alt={getTranslatedContent('title')}
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="p-6 text-black">
          <h2 className={`text-3xl font-bold mb-4 ${getFontClass()}`}>
            {getTranslatedContent('title')}
          </h2>
          <p className={`text-xl leading-relaxed ${getFontClass()}`}>
            {getTranslatedContent('description')}
          </p>
        </div>
      </div>
    </div>
  );
};

const Card: React.FC<{ 
  feature: Feature; 
  translations?: Translation; 
  locale: string;
  onClick: () => void; 
}> = ({ 
  feature, 
  translations, 
  locale,
  onClick
}) => {
  const imageUrl = useMemo(() => {
    try {
      return pb.files.getURL(feature, feature.image);
    } catch (error) {
      console.error('Error generating image URL:', error);
      return '/placeholder.jpg';
    }
  }, [feature]);

  const getTranslatedContent = (field: 'title' | 'description') => {
    if (locale === 'en') {
      return feature[field];
    }
    return translations?.translations?.[locale]?.[field] || feature[field];
  };

  const getFontClass = () => {
    return locale === 'np' ? 'font-noto' : 'font-slate';
  };

  return (
    <div 
      className="relative w-[280px] h-[400px] flex-shrink-0 rounded-lg overflow-hidden mt-[36px] cursor-pointer transition-transform duration-300 hover:scale-[1.02] hover:shadow-lg"
      onClick={onClick}
    >
      <div className="absolute inset-0">
        <img 
          src={imageUrl} 
          alt={getTranslatedContent('title')}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = '/placeholder.jpg';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/20 to-black/90" />
      </div>
      
      <div className="relative z-10 flex flex-col justify-end h-full p-6 text-white">
        <h3 className={`text-3xl font-bold mb-3 ${getFontClass()}`}>
          {getTranslatedContent('title')}
        </h3>
        <p className={`text-lg leading-snug ${getFontClass()}`}>
          {getTranslatedContent('description')}
        </p>
        
        {/* Simple text indicator that it's clickable */}
        <div className="flex items-center mt-3 text-sm text-white/80">
          <span>Tap to view</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

export const FeatureCard: React.FC<FeatureCardProps> = ({ productId }) => {
  const [searchParams] = useSearchParams();
  const [features, setFeatures] = useState<Feature[]>([]);
  const [translations, setTranslations] = useState<{ [key: string]: Translation }>({});
  const [loading, setLoading] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const locale = searchParams.get('locale') || 'en';

  useEffect(() => {
    const loadFeatures = async () => {
      try {
        const records = await pb.collection('features').getList<Feature>(1, 50, {
          filter: `product ~ "${productId}"`,
          sort: 'order',
        });
        setFeatures(records.items);

        // Fetch translations for each feature
        const translationsMap: { [key: string]: Translation } = {};
        for (const feature of records.items) {
          try {
            const translation = await pb.collection('translations').getFirstListItem<Translation>(
              `collection_name = "features" && record_id = "${feature.id}"`
            );
            translationsMap[feature.id] = translation;
          } catch (error) {
            console.error('Translation not found for feature:', feature.id);
          }
        }
        setTranslations(translationsMap);
      } catch (error: any) {
        console.error('Error loading features:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (productId) {
      loadFeatures();
    }
  }, [productId]);

  if (loading) return null;
  if (features.length === 0) return null;

  return (
    <>
      <div className="w-full overflow-x-auto no-scrollbar">
        <div className="flex gap-4 snap-x snap-mandatory pb-2 px-4">
          {features.map((feature) => (
            <div 
              key={feature.id} 
              className="snap-center shrink-0"
            >
              <Card 
                feature={feature} 
                translations={translations[feature.id]}
                locale={locale}
                onClick={() => setSelectedFeature(feature)}
              />
            </div>
          ))}
          <div className="w-1 shrink-0" aria-hidden="true" />
        </div>
      </div>

      {selectedFeature && (
        <FeatureModal
          feature={selectedFeature}
          translations={translations[selectedFeature.id]}
          locale={locale}
          onClose={() => setSelectedFeature(null)}
        />
      )}
    </>
  );
};